import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get, set } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineAddRoad,
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineCheckCircle,
  MdOutlineSubtitles,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  FRANCE_DEPARTMENTS,
  GENDER,
  RELATIONSHIP,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Partner = ({ setPercent, isFieldRequired, bindField, initialValues }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const { t: p } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const COUNTRIES = p("DETAIL.COUNTRIES", { returnObjects: true });
  const birthDate = get(
    initialValues,
    "spouse.identity.user.birthDate",
    undefined,
  );

  const birthNamePath = "spouse.identity.user.birthName";
  const birthNameField = bindField(birthNamePath, {
    label: SUBSCRIPTION.FORM.BIRTH_NAME.LABEL,
    required: isFieldRequired(birthNamePath),
    onBlur: () => setPercent(initialValues),
  });

  const lastnamePath = "spouse.identity.user.lastname";
  const lastnameField = bindField(lastnamePath, {
    label: SUBSCRIPTION.FORM.LAST_NAME.LABEL,
    required: isFieldRequired(lastnamePath),
    onBlur: () => setPercent(initialValues),
  });

  const firstnamePath = "spouse.identity.user.firstname";
  const firstnameField = bindField(firstnamePath, {
    label: SUBSCRIPTION.FORM.FIRST_NAME.LABEL,
    required: isFieldRequired(firstnamePath),
    onBlur: () => setPercent(initialValues),
  });

  const genderPath = "spouse.identity.user.gender";
  const genderField = bindField(genderPath, {
    label: SUBSCRIPTION.FORM.GENDER.LABEL,
    required: isFieldRequired(genderPath),
    onBlur: () => setPercent(initialValues),
  });

  const socialSecurityNumberPath = "spouse.identity.socialSecurityNumber";
  const socialSecurityNumberField = bindField(socialSecurityNumberPath, {
    label: SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.LABEL,
    required: isFieldRequired(socialSecurityNumberPath),
    onBlur: () => setPercent(initialValues),
  });

  const birthCountryPath = `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`;
  const birthCountryField = bindField(birthCountryPath, {
    label: SUBSCRIPTION.FORM.BIRTH_COUNTRY.LABEL,
    required: isFieldRequired(birthCountryPath),
    onChange: (value) => {
      if (value !== "France") {
        set(
          initialValues,
          `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
          "Autre/Etranger",
        );
      }
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });

  const birthCityPath = `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`;
  const birthCityField = bindField(birthCityPath, {
    label: SUBSCRIPTION.FORM.BIRTH_CITY.LABEL,
    required: isFieldRequired(birthCityPath),
    onBlur: () => setPercent(initialValues),
  });

  const birthDepartmentPath = `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`;
  const birthDepartmentField = bindField(birthDepartmentPath, {
    label: SUBSCRIPTION.FORM.BIRTH_DEPARTMENT.LABEL,
    required: isFieldRequired(birthDepartmentPath),
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const teletransmissionPath = "spouse.identity.fields.teletransmission";
  const teletransmissionField = bindField(teletransmissionPath, {
    label: SUBSCRIPTION.FORM.TELETRANSMISSION.LABEL,
    extractValue: (checked) => !checked,
    onChange: () => setPercent(initialValues),
  });

  return (
    get(initialValues, "project.contact.relatedPersons", []).find(
      (person) => person.relationship === RELATIONSHIP.SPOUSE,
    ) && (
      <div
        className="subscription-form--wrapper"
        id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PARTNER}-section`}
      >
        <Form
          type="vertical"
          title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PARTNER.TITLE}
          subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PARTNER.SUBTITLE}
        >
          <Row gutter={[16, 16]}>
            <Col lg={24} xs={24}>
              <FormField
                path={birthNameField.path}
                label={birthNameField.label}
                required={birthNameField.required}
                disabled={birthNameField.disabled}
              >
                {(field) => (
                  <Input
                    placeholder={SUBSCRIPTION.FORM.BIRTH_NAME.PLACEHOLDER}
                    label={birthNameField.label}
                    icon={<MdOutlineBadge size={16} />}
                    type="custom"
                    normalizeText={true}
                    defaultValue={birthNameField.defaultValue}
                    {...field}
                    onChange={birthNameField.onChange}
                    onBlur={birthNameField.onBlur}
                    optional={!birthNameField.required}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={lastnameField.path}
                label={lastnameField.label}
                required={lastnameField.required}
                disabled={lastnameField.disabled}
              >
                {(field) => (
                  <Input
                    placeholder={SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER}
                    label={lastnameField.label}
                    icon={<MdOutlineBadge size={16} />}
                    type="custom"
                    normalizeText={true}
                    defaultValue={lastnameField.defaultValue}
                    {...field}
                    onChange={lastnameField.onChange}
                    onBlur={lastnameField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={firstnameField.path}
                label={firstnameField.label}
                required={firstnameField.required}
                disabled={firstnameField.disabled}
              >
                {(field) => (
                  <Input
                    label={firstnameField.label}
                    icon={<MdOutlineBadge size={16} />}
                    placeholder={SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER}
                    type="custom"
                    normalizeText={true}
                    defaultValue={firstnameField.defaultValue}
                    {...field}
                    onChange={firstnameField.onChange}
                    onBlur={firstnameField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={genderField.path}
                label={genderField.label}
                required={genderField.required}
                disabled={genderField.disabled}
              >
                {(field) => (
                  <Input
                    type="radio"
                    variant="group"
                    label={genderField.label}
                    values={[
                      {
                        label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MR,
                        value: GENDER.MALE,
                      },
                      {
                        label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MRS,
                        value: GENDER.FEMALE,
                      },
                    ]}
                    icon={<MdOutlineBadge size={16} />}
                    value={get(initialValues, genderPath)}
                    {...field}
                    onChange={genderField.onChange}
                    onBlur={genderField.onBlur}
                    optional={!genderField.required}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={socialSecurityNumberField.path}
                label={socialSecurityNumberField.label}
                required={socialSecurityNumberField.required}
                disabled={socialSecurityNumberField.disabled}
              >
                {(field) => (
                  <Input
                    label={socialSecurityNumberField.label}
                    icon={<MdOutlineSubtitles size={16} />}
                    placeholder={
                      SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.PLACEHOLDER
                    }
                    type="custom"
                    trim={true}
                    defaultValue={socialSecurityNumberField.defaultValue}
                    {...field}
                    onChange={socialSecurityNumberField.onChange}
                    onBlur={socialSecurityNumberField.onBlur}
                    optional={!socialSecurityNumberField.required}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <Input
                label={SUBSCRIPTION.FORM.BIRTH_DATE.LABEL}
                icon={<MdOutlineCake size={16} />}
                placeholder={SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER}
                defaultValue={birthDate && dayjs(birthDate)}
                type="date"
                variant="simple"
                format={DATE_FORMAT}
                disabled
              />
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={birthCountryField.path}
                label={birthCountryField.label}
                required={birthCountryField.required}
                disabled={birthCountryField.disabled}
              >
                {(field) => (
                  <Select
                    label={birthCountryField.label}
                    icon={<MdOutlineAddRoad size={16} />}
                    placeholder={SUBSCRIPTION.FORM.BIRTH_COUNTRY.PLACEHOLDER}
                    options={COUNTRIES.map((name) => ({
                      label: name,
                      value: name,
                    }))}
                    showSearch
                    allowClear
                    defaultValue={birthCountryField.defaultValue}
                    {...field}
                    onChange={birthCountryField.onChange}
                    onBlur={birthCountryField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={birthCityField.path}
                label={birthCityField.label}
                required={birthCityField.required}
                disabled={birthCityField.disabled}
              >
                {(field) => (
                  <Input
                    label={birthCityField.label}
                    icon={<MdOutlineAddRoad size={16} />}
                    placeholder={SUBSCRIPTION.FORM.BIRTH_CITY.PLACEHOLDER}
                    type="custom"
                    normalizeText={true}
                    defaultValue={birthCityField.defaultValue}
                    {...field}
                    onChange={birthCityField.onChange}
                    onBlur={birthCityField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={birthDepartmentField.path}
                label={birthDepartmentField.label}
                required={birthDepartmentField.required}
                disabled={birthDepartmentField.disabled}
              >
                {(field) => (
                  <Select
                    label={birthDepartmentField.label}
                    icon={<MdOutlineAddRoad size={16} />}
                    placeholder={SUBSCRIPTION.FORM.BIRTH_DEPARTMENT.PLACEHOLDER}
                    showSearch
                    allowClear
                    options={FRANCE_DEPARTMENTS.map(({ code, name }) => ({
                      label: `${code && `(${code})`} ${name}`,
                      value: name,
                    }))}
                    value={get(initialValues, birthDepartmentPath)}
                    defaultValue={birthDepartmentField.defaultValue}
                    {...field}
                    onChange={birthDepartmentField.onChange}
                    onBlur={birthDepartmentField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={teletransmissionField.path}
                label={teletransmissionField.label}
                required={teletransmissionField.required}
                disabled={teletransmissionField.disabled}
              >
                {(field) => (
                  <Input
                    type="switch"
                    label={teletransmissionField.label}
                    icon={<MdOutlineCheckCircle size={16} />}
                    defaultChecked={
                      get(initialValues, teletransmissionPath, true) === false
                    }
                    {...field}
                    onChange={teletransmissionField.onChange}
                    onBlur={teletransmissionField.onBlur}
                  />
                )}
              </FormField>
            </Col>
          </Row>
        </Form>
      </div>
    )
  );
};

export default Partner;
